"use client";

import type { FC } from 'react';
import { useAuiState, useMessageTiming } from '@assistant-ui/react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip';
import { cn } from '@/shared/lib/cn';

function formatMs(ms: number | undefined): string {
  if (ms === undefined) return '—';
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatMessageTimestamp(date: Date): string {
  const hours = date.getHours();
  const hour12 = hours % 12 || 12;
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  return `${monthNames[date.getMonth()]}-${String(date.getDate()).padStart(2, '0')}-${date.getFullYear()} ${String(hour12).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')} ${meridiem}`;
}

export const MessageTiming: FC<{ className?: string }> = ({ className }) => {
  const timing = useMessageTiming();
  const running = useAuiState((state) => state.message.status?.type === 'running');

  // RunningActionBarTimer (ChatMessage.tsx) is the single live-run clock in
  // this same action-bar slot, ticking from this message's own
  // streamStartTime. Rendering this ticking clock too showed two independent
  // counters at once during a run -- so this only renders the final, settled
  // duration once the message is done.
  if (!timing || running) return null;
  const totalStreamTime = timing?.totalStreamTime;
  if (totalStreamTime === undefined) return null;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label="Message timing" className={cn('rounded-md p-1 font-mono text-xs tabular-nums text-muted-foreground hover:bg-accent hover:text-accent-foreground', className)}>
            {formatMs(totalStreamTime)}{running ? '…' : ''}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          <div className="grid min-w-[8.75rem] gap-1.5 text-xs">
            {timing.firstTokenTime !== undefined && <div className="flex justify-between gap-4"><span className="text-muted-foreground">First token</span><span className="font-mono">{formatMs(timing.firstTokenTime)}</span></div>}
            <div className="flex justify-between gap-4"><span className="text-muted-foreground">Total</span><span className="font-mono">{formatMs(totalStreamTime)}</span></div>
            {timing.tokensPerSecond !== undefined && <div className="flex justify-between gap-4"><span className="text-muted-foreground">Speed</span><span className="font-mono">{timing.tokensPerSecond.toFixed(1)} tok/s</span></div>}
            <div className="flex justify-between gap-4"><span className="text-muted-foreground">Chunks</span><span className="font-mono">{timing.totalChunks}</span></div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const MessageTimestamp: FC<{ className?: string }> = ({ className }) => {
  const createdAt = useAuiState((state) => state.message.createdAt);
  if (!createdAt || Number.isNaN(createdAt.getTime())) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <time
            className={cn('mt-1 cursor-help px-1 text-[11px] text-muted-foreground/70', className)}
            dateTime={createdAt.toISOString()}
          >
            {formatMessageTimestamp(createdAt)}
          </time>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {createdAt.toISOString()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
